import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft, FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { getItemsCached, getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import ItemSelect from '../../components/ItemSelect';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import {
  createTransaction, updateTransaction, deleteTransaction,
  updateTransactionStatus
} from '../../utils/transactionHelper';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage, TranEmptyState,
  TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn, SummaryBar,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton,
  FormCard, SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea,
  InfoGrid, InfoItem, NotesText,
  LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd, LineInput, RemoveLineBtn,
  SortableTh, SortContext
} from '../../components/SharedStyles';

const TRAN_TYPE_CODE = 'OGP';

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Pending', 'Dispatched', 'Closed']
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, uom: '', unitCode: '', remarks: '' };
const STATUS_COLORS = { Pending: colors.warning.main, Dispatched: colors.info.main, Closed: colors.success.main, Cancelled: colors.error.main };

const OutwardGatePass = () => {
  const { activeCompany, currentUser, companyUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('store', 'outwardGatePass');
  const isAdmin = companyUser?.role === 'admin';
  const showBranchCol = activeBranch && activeBranch.id === 'all';
  const [viewMode, setViewMode] = useState('list');
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0], docTime: new Date().toTimeString().slice(0, 5),
    destination: '', vehicleNo: '', driverName: '', purpose: 'Sale', authorizedBy: '', notes: '', status: 'Pending',
    locationId: '', locationName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [printConfig, setPrintConfig] = useState(null);

  // The record list itself loads inside useDocListPagination above
  // (loadRecords = its refresh). The old full-history getTransactions load lived here.

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) { console.error('Error:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadItems(); loadLocations(); loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [loadItems, loadLocations]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) { const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.docNumber?.toLowerCase().includes(q) || r.destination?.toLowerCase().includes(q) || r.vehicleNo?.toLowerCase().includes(q));
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev]; updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'salePrice' });
          updated[index].uom = patch.unitCode;
          updated[index].unitCode = patch.unitCode;
        }
      }
      if (field === 'itemId' && value) { const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'salePrice', defaultUnitField: 'defaultSaleUnit' });
          updated[index].uom = seeded.unitCode || item.unitOfMeasure || 'Pcs';
          updated[index].unitCode = seeded.unitCode || '';
        }
      }
      return updated;
    });
  };
  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || !!line.description || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Add a new line item?')) {
        pendingFocusRowRef.current = lineItems.length;
        addLine();
      }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], docTime: new Date().toTimeString().slice(0, 5), destination: '', vehicleNo: '', driverName: '', purpose: 'Sale', authorizedBy: '', notes: '', status: 'Pending', locationId: '', locationName: '' });
    setLineItems([{ ...EMPTY_LINE }]); setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], docTime: rec.docTime || '', destination: rec.destination || '', vehicleNo: rec.vehicleNo || '', driverName: rec.driverName || '', purpose: rec.purpose || 'Sale', authorizedBy: rec.authorizedBy || '', notes: rec.notes || '', status: rec.status || 'Pending', locationId: rec.locationId || '', locationName: rec.locationName || '' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!formData.authorizedBy.trim()) { alert('Please enter Authorized By'); return; }
    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one item'); return; }

    // Financial year validation
    if (!activeFinancialYear?.id) {
      alert('No active financial year. Please select a financial year.');
      return;
    }

    setSaving(true);
    try {
      const data = {
        docDate: formData.docDate,
        docTime: formData.docTime,
        destination: formData.destination,
        vehicleNo: formData.vehicleNo,
        driverName: formData.driverName,
        purpose: formData.purpose,
        authorizedBy: formData.authorizedBy,
        locationId: formData.locationId,
        locationName: formData.locationName,
        notes: formData.notes,
        status: saveStatus || 'Pending',
        lines: validLines.map((l, i) => {
          const item = items.find(it => it.id === l.itemId);
          return { ...l, lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
        }),
        financialYearId: activeFinancialYear.id,
        branchId: activeBranch?.id || null,
        branchName: activeBranch?.branchName || null
      };

      let transactionId;
      let newTranData = null;

      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
        transactionId = editingRecord.id;
      } else {
        newTranData = await createTransaction(activeCompany.id, { ...data, tranTypeCode: TRAN_TYPE_CODE }, currentUser.uid);
        transactionId = newTranData.id;
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: transactionId }
        : { ...newTranData, status: data.status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === transactionId ? savedRecord : r)
        : [savedRecord, ...prev]);

      setViewMode('list');
      resetForm();

      // Background refresh — reconciles with server data without blocking UI.
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) { console.error('Error:', error); alert('Error saving: ' + error.message); } finally { setSaving(false); }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (rec.status !== 'Pending' && !isAdmin) { alert('Only pending records can be deleted'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error:', error); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (rec.status === 'Cancelled' || rec.status === 'Closed') { alert('Cannot cancel this record'); return; }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('outwardGatePass', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar><SearchBox><FiSearch /><input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></SearchBox>
        <FilterGroup><StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="All">All Status</option><option value="Pending">Pending</option><option value="Dispatched">Dispatched</option><option value="Closed">Closed</option><option value="Cancelled">Cancelled</option>
        </StatusFilterSelect>{canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Gate Pass</TranAddButton>}</FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Outward Gate Passes</h3><p>{records.length === 0 ? 'Create your first gate pass' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Gate Pass</TranAddButton>}</TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr><SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="destination">Destination</SortableTh><SortableTh field="purpose">Purpose</SortableTh><SortableTh field="vehicleNo">Vehicle</SortableTh><SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh><Th style={{ textAlign: 'center' }}>Actions</Th></tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}><Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>{showBranchCol && <Td>{rec.branchName || '-'}</Td>}<Td>{rec.destination || '-'}</Td><Td>{rec.purpose}</Td><Td>{rec.vehicleNo || '-'}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && (rec.status === 'Pending' || isAdmin) && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && rec.status !== 'Closed' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}>
                    <FiXCircle />
                  </IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ))}</tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar><span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong></span><span>Pending: <strong>{listSummary.counts.Pending || 0}</strong> | Dispatched: <strong>{listSummary.counts.Dispatched || 0}</strong> | Closed: <strong>{listSummary.counts.Closed || 0}</strong></span></SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null; const rec = viewingRecord;
    return (<>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
        <FormActions>{canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
          <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton></FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Gate Pass Details</SectionTitle><InfoGrid>
        <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem><InfoItem><label>Date & Time</label><span>{rec.docDate} {rec.docTime}</span></InfoItem>
        <InfoItem><label>Destination</label><span>{rec.destination || '-'}</span></InfoItem><InfoItem><label>Purpose</label><span>{rec.purpose}</span></InfoItem>
        <InfoItem><label>Vehicle No</label><span>{rec.vehicleNo || '-'}</span></InfoItem><InfoItem><label>Driver Name</label><span>{rec.driverName || '-'}</span></InfoItem>
        <InfoItem><label>Authorized By</label><span>{rec.authorizedBy}</span></InfoItem>
        <InfoItem><label>Location</label><span>{rec.locationName || '-'}</span></InfoItem>
      </InfoGrid></FormCard>
      <FormCard><SectionTitle>Items</SectionTitle><TableContainer><Table><thead><tr><Th>#</Th><Th>Description</Th><Th>Qty</Th><Th>UOM</Th><Th>Remarks</Th></tr></thead><tbody>
        {(rec.lines || []).map((line, i) => (<Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td><Td>{line.quantity}</Td><Td>{line.uom || '-'}</Td><Td>{line.remarks || '-'}</Td></Tr>))}
      </tbody></Table></TableContainer></FormCard>
      {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
    </>);
  };

  const renderFormView = () => (
    <>
      <FormHeader><BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Outward Gate Pass'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Pending')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save'}</SaveButton>}
          {canEdit && (formData.status === 'Pending' || !editingRecord) && <SaveButton $approve onClick={() => handleSave('Dispatched')} disabled={saving}><FiCheck /> Dispatch</SaveButton>}
        </FormActions>
      </FormHeader>
      <FormCard><SectionTitle>Gate Pass Details</SectionTitle><FormGrid>
        <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
        <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
        <FormGroup><Label>Time *</Label><Input type="time" value={formData.docTime} onChange={e => handleHeaderChange('docTime', e.target.value)} /></FormGroup>
        <FormGroup><Label>Purpose *</Label><Select value={formData.purpose} onChange={e => handleHeaderChange('purpose', e.target.value)}>
          <option value="Sale">Sale</option><option value="Return">Return</option><option value="Transfer">Transfer</option><option value="Repair">Repair</option><option value="Other">Other</option>
        </Select></FormGroup>
        <FormGroup $span2><Label>Destination *</Label><Input value={formData.destination} onChange={e => handleHeaderChange('destination', e.target.value)} placeholder="Destination address or location" /></FormGroup>
        <FormGroup><Label>Vehicle No</Label><Input value={formData.vehicleNo} onChange={e => handleHeaderChange('vehicleNo', e.target.value)} placeholder="Vehicle number" /></FormGroup>
        <FormGroup><Label>Driver Name</Label><Input value={formData.driverName} onChange={e => handleHeaderChange('driverName', e.target.value)} placeholder="Driver name" /></FormGroup>
        <FormGroup><Label>Authorized By *</Label><Input value={formData.authorizedBy} onChange={e => handleHeaderChange('authorizedBy', e.target.value)} placeholder="Name of authorizer" /></FormGroup>
        <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
        <FormGroup>
          <Label>{reqLabel(activeCompany, 'Location')}</Label>
          <Select value={formData.locationId} onChange={e => {
            const loc = locations.find(l => l.id === e.target.value);
            handleHeaderChange('locationId', e.target.value);
            handleHeaderChange('locationName', loc?.locationName || '');
          }}>
            <option value="">-- Select Location --</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.locationName}</option>
            ))}
          </Select>
        </FormGroup>
      </FormGrid></FormCard>
      <FormCard>
        <LineItemsHeader><SectionTitle style={{ margin: 0 }}>Items</SectionTitle><AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton></LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr><LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh><LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh><LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Description</LineTh><LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh><LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>UOM</LineTh><LineTh style={{ width: 180, minWidth: 180, maxWidth: 180 }}>Remarks</LineTh><LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh></tr></thead><tbody>
          {lineItems.map((line, index) => (
            <tr key={index}><LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} /></LineTd>
              <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
              <LineTd><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd>{(() => {
                const itm = items.find(i => i.id === line.itemId);
                const itmUnits = itm && Array.isArray(itm.units) ? itm.units : [];
                if (itmUnits.length > 1) {
                  return <LineUnitPicker item={itm} line={{ ...line, unitOfMeasure: line.uom }} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} />;
                }
                return <LineInput value={line.uom} onChange={e => handleLineChange(index, 'uom', e.target.value)} placeholder="UOM" />;
              })()}</LineTd>
              <LineTd><LineInput value={line.remarks} onChange={e => handleLineChange(index, 'remarks', e.target.value)} onKeyDown={(e) => handleLastFieldEnter(e, index)} placeholder="Condition/remarks" /></LineTd>
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>
          ))}
        </tbody></LineTable></LineTableContainer>
      </FormCard>
      <FormCard><SectionTitle>Notes</SectionTitle><FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid></FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: canEdit ? handleCreate : undefined,
    onSave: canEdit ? () => handleSave('Dispatched') : undefined,
    viewMode,
    saving
  });

  return (
    <PageLayout pageTitle="Outward Gate Pass" breadcrumb="Store / Outward Gate Pass">
      {viewMode === 'list' && renderListView()}{viewMode === 'form' && renderFormView()}{viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default OutwardGatePass;
